// @flow
/* -----------------Globals--------------- */
import React, { useState } from 'react';

/* -----------------Third parties--------------- */
import { range } from 'lodash';

/* -----------------UI--------------- */
import {
  Box,
  Grid,
  ListSubheader,
  ListItem,
  ListItemSecondaryAction,
  Collapse,
  Typography,
} from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { withStyles, useTheme } from '@material-ui/core/styles';
import { ChevronDown, ChevronUp } from 'mdi-material-ui';

/* -----------------Types--------------- */
import type {
  FAQ,
} from 'common/store/types';

/* -----------------Styles--------------- */
const styles = theme => ({
  prompt: {
    fontWeight: theme.fontWeights.semiBold,
    padding: theme.spacing(0, 1, 0, 1),
  },
  answer: {
    padding: theme.spacing(1, 3, 3, 3),
    lineHeight: 1.5,
    fontWeight: theme.fontWeights.light,
  },
  listItemSecondaryAction: {
    color: theme.palette.grey[600],
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingRight: 0,
  },
  questionGridItem: {
    width: '100%',
  },
  listItem: {
    width: '100%',
    borderRadius: theme.shape.borderRadius,
  },
});

/* -----------------Type declarations--------------- */
type Classes = {
  question: string,
  prompt: string,
  answer: string,
  questionGridItem: string,
  listItemSecondaryAction: string,
  listItem: string,
}

/* -----------------Props--------------- */
type Props = {
  questions: FAQ[],
  singleColumnOnly?: boolean,
  classes: Classes,
}

type QuestionProps = {
  prompt: string,
  answer: string,
}

/* -----------------Component--------------- */
const Faq = (props: Props) => {
  const {
    questions,
    singleColumnOnly = false,
    classes,
  } = props;

  const theme = useTheme();
  const singleColumn = useMediaQuery(theme.breakpoints.down('sm')) || singleColumnOnly;
  const numColumns = singleColumn ? 1 : 2;
  const getNthItems = (array, nth = 1, start = 0) => array.filter(
    (_, i) => i % nth === start && i >= start,
  );

  const Question = (questionProps: QuestionProps) => {
    const { prompt, answer } = questionProps;

    const [open, setOpen] = useState(false);

    const handleSetOpen = () => {
      setOpen(!open);
    };

    return (
      <>
        <ListItem
          button
          ContainerComponent="div"
          classes={{ container: classes.listItem }}
          onClick={handleSetOpen}
        >
          <Typography
            variant="subtitle1"
            className={classes.prompt}
          >
            {prompt}
          </Typography>
          <ListItemSecondaryAction
            classes={{ root: classes.listItemSecondaryAction }}
          >
            {open ? <ChevronUp /> : <ChevronDown />}
          </ListItemSecondaryAction>
        </ListItem>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Typography
            variant="body2"
            className={classes.answer}
          >
            {answer}
          </Typography>
        </Collapse>
      </>
    );
  };

  return (
    <Box py={6}>
      <ListSubheader
        disableSticky
      >
        Frequently Asked Questions
      </ListSubheader>
      <Grid
        container
        item
        spacing={2}
      >
        {range(numColumns).map((i, colIndex) => (
          <Grid
            key={colIndex.toString()}
            container
            wrap="nowrap"
            justifyContent="flex-start"
            item
            direction="column"
            spacing={0}
            xs={12}
            sm={12}
            md={singleColumn ? 12 : 6}
          >
            {getNthItems(questions, numColumns, i).map(({
              prompt,
              answer,
            }, index) => (
              <Grid
                item
                key={index.toString()}
                className={classes.questionGridItem}
                container
              >
                <Question
                  prompt={prompt}
                  answer={answer}
                />
              </Grid>
            ))}
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default withStyles(styles)(Faq);
